
import React from 'react';
import { Button } from './Button';
import { Crown, LayoutGrid, Zap, Coffee, ArrowRight } from 'lucide-react';

interface WelcomeModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const WelcomeModal: React.FC<WelcomeModalProps> = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm animate-fade-in text-gray-900 dark:text-dark-text">
      <div className="bg-white dark:bg-dark-surface rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden border border-gray-100 dark:border-dark-border">
        
        {/* Banner Image / Header */}
        <div className="bg-blue-600 h-32 relative flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-600 to-indigo-700 opacity-90"></div>
          <div className="absolute top-[-20px] left-[-20px] w-24 h-24 bg-white/10 rounded-full blur-xl"></div>
          <div className="absolute bottom-[-20px] right-[-20px] w-32 h-32 bg-yellow-400/20 rounded-full blur-xl"></div>
          
          <div className="relative z-10 text-center">
            <img src="logo.png" className="w-12 h-12 bg-white rounded-xl shadow-lg mx-auto mb-2 object-cover" alt="Eisenhower Pro Logo" />
            <h2 className="text-white font-bold text-lg tracking-wide">Eisenhower Pro</h2>
          </div>
        </div>

        <div className="p-8">
          <h1 className="text-2xl font-bold text-center mb-2">Welcome to Productivity!</h1>
          <p className="text-gray-500 dark:text-gray-400 text-center mb-8 text-sm">
            We've set up your board with 4 sample tasks to get you started. Here is how to win the game:
          </p>

          <div className="space-y-6 mb-8">
            <div className="flex items-start gap-4">
               <div className="p-2 bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 rounded-lg">
                 <Crown size={24} />
               </div>
               <div>
                 <h3 className="font-bold text-sm">Earn XP & Level Up</h3>
                 <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Complete tasks to gain XP. Unlock new Themes, Soundscapes, and Analytics as you level up.
                 </p>
               </div>
            </div>

            <div className="flex items-start gap-4">
               <div className="p-2 bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-lg">
                 <LayoutGrid size={24} />
               </div>
               <div>
                 <h3 className="font-bold text-sm">Prioritize the Matrix</h3>
                 <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    <span className="font-semibold text-blue-600 dark:text-blue-400">Schedule (Q2)</span> tasks are for long-term growth and award <span className="font-bold">2x XP</span>. Don't just do urgent work!
                 </p>
               </div>
            </div>

            <div className="flex items-start gap-4">
               <div className="p-2 bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 rounded-lg">
                 <Zap size={24} />
               </div>
               <div>
                 <h3 className="font-bold text-sm">Deep Focus Mode</h3>
                 <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Click the <Zap size={10} className="inline"/> icon on any task to start the timer, block distractions, and listen to focus sounds.
                 </p>
               </div>
            </div>
          </div>

          <Button size="lg" className="w-full group" onClick={onClose}>
             Let's Get Started <ArrowRight size={18} className="ml-2 group-hover:translate-x-1 transition-transform" />
          </Button>
        </div>
      </div>
    </div>
  );
};
